import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Badge,
  Dropdown,
  Menu,
  Modal,
  Form,
  message,
  Typography,
  Avatar,
  Tooltip,
  Empty,
  Skeleton,
  Divider,
  Timeline,
  Alert,
  Tabs
} from 'antd';
import {
  BugOutlined,
  FilterOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SlackOutlined,
  CalendarOutlined,
  SearchOutlined,
  MoreOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ApiOutlined,
  TagsOutlined,
  TeamOutlined,
  RocketOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import apiClient from '../services/axiosConfig';
import { useTheme } from '../App';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea: AntTextArea } = Input;

const IssuesSection = ({ refreshTrigger }) => {
  const { theme, currentTheme } = useTheme();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {},
    byPriority: {},
    openIssues: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    group: '',
    search: '',
    page: 1,
    limit: 20
  });
  
  // Modal states
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch issues with filters
  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: filters.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.group && { group: filters.group })
      };
      
      const response = await apiClient.get(`${API_BASE_URL}/issues`, { params });
      setIssues(response.data.data);
    } catch (error) {
      message.error('Failed to fetch issues');
    }
    setLoading(false);
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/issues/stats`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchIssues();
    fetchStats();
  }, [filters, refreshTrigger]);

  // Handle status update
  const handleStatusUpdate = async (issueId, newStatus) => {
    try {
      await apiClient.patch(`${API_BASE_URL}/issues/${issueId}`, { status: newStatus });
      message.success(`Issue ${newStatus === 'resolved' ? 'resolved' : 'updated'} successfully`);
      fetchIssues();
      fetchStats();
    } catch (error) {
      message.error('Failed to update issue status');
    }
  };

  // Handle resolve with notes
  const handleResolve = async (values) => {
    try {
      await apiClient.patch(`${API_BASE_URL}/issues/${selectedIssue._id}`, {
        status: 'resolved',
        resolution: {
          resolvedBy: {
            userId: 'current-user-id', // Get from your auth context
            name: 'Current User' // Get from your auth context
          },
          notes: values.resolutionNotes
        }
      });
      message.success('Issue resolved successfully');
      setResolveModalVisible(false);
      form.resetFields();
      fetchIssues();
      fetchStats();
    } catch (error) {
      message.error('Failed to resolve issue');
    }
  };

  // Handle delete issue
  const handleDeleteIssue = async (issueId) => {
    Modal.confirm({
      title: 'Delete Issue',
      content: 'Are you sure you want to delete this issue? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await apiClient.delete(`${API_BASE_URL}/issues/${issueId}`);
          message.success('Issue deleted successfully');
          fetchIssues();
          fetchStats();
        } catch (error) {
          message.error('Failed to delete issue');
        }
      }
    });
  };

  // Priority color mapping
  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#ff4d4f',
      high: '#faad14',
      medium: '#1890ff',
      low: '#52c41a'
    };
    return colors[priority] || '#d9d9d9';
  };

  // Issue type icon mapping
  const getIssueTypeIcon = (type) => {
    const icons = {
      'wrong-structure': <ExclamationCircleOutlined />,
      'missing-fields': <InfoCircleOutlined />,
      'incorrect-data': <WarningOutlined />,
      'business-logic': <ApiOutlined />,
      'documentation-mismatch': <MessageOutlined />,
      'performance': <ClockCircleOutlined />,
      'other': <BugOutlined />
    };
    return icons[type] || <BugOutlined />;
  };

  // Format issue type for display
  const formatIssueType = (type) => {
    return type.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const columns = [
    {
      title: 'Issue',
      key: 'issue',
      width: '20%',
      render: (_, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ marginRight: 8, color: theme.primary }}>
              {getIssueTypeIcon(record.issue.type)}
            </span>
            <Text strong style={{ fontSize: '13px' }}>{record.issue.title}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.apiDetails.group} / {record.apiDetails.name}
          </Text>
        </div>
      )
    },
    {
        title: 'API',
        key: 'api',
        width: '20%',
        render: (_, record) => (
          <div>
            <Tag color={
              record.apiDetails.method === 'GET' ? 'green' :
              record.apiDetails.method === 'POST' ? 'blue' :
              record.apiDetails.method === 'PUT' ? 'orange' :
              record.apiDetails.method === 'DELETE' ? 'red' : 'default'
            } style={{ fontSize: '8px' }}>
              {record.apiDetails.method}
            </Tag>
            <div style={{ 
              fontSize: '11px', 
              marginTop: '2px',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              lineHeight: '1.2'
            }}>
              {record.apiDetails.endpoint}
            </div>
          </div>
        )
      },
    {
      title: 'Priority',
      dataIndex: ['issue', 'priority'],
      key: 'priority',
      width: '8%',
      filters: [
        { text: 'Critical', value: 'critical' },
        { text: 'High', value: 'high' },
        { text: 'Medium', value: 'medium' },
        { text: 'Low', value: 'low' }
      ],
      onFilter: (value, record) => record.issue.priority === value,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} style={{ fontSize: '11px' }}>
          {priority.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '6%',
      filters: [
        { text: 'Open', value: 'open' },
        { text: 'Resolved', value: 'resolved' },
        { text: "Won't Fix", value: 'wont-fix' }
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Badge
          status={
            status === 'open' ? 'processing' :
            status === 'resolved' ? 'success' : 'default'
          }
          text={<span style={{ fontSize: '11px' }}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>}
        />
      )
    },
    {
      title: 'Reporter',
      key: 'reporter',
      width: '12%',
      render: (_, record) => (
        <Tooltip title={record.reporter.email}>
          <Space size="small">
            <Avatar size="small" icon={<UserOutlined />} />
            <Text style={{ fontSize: '11px' }}>{record.reporter.name}</Text>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Age',
      key: 'age',
      width: '8%',
      sorter: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {moment(record.createdAt).fromNow()}
        </Text>
      )
    },
    {
      title: 'Integrations',
      key: 'integrations',
      width: '9%',
      render: (_, record) => (
        <Space size="small">
          {record.integrations.slack.sentToSlack && (
            <Tooltip title="Sent to Slack">
              <SlackOutlined style={{ color: '#4A154B', fontSize: '16px' }} />
            </Tooltip>
          )}
          {record.integrations.jira.sentToJira && (
            <Tooltip title="Created in Jira">
              <img 
                src="https://cdn.worldvectorlogo.com/logos/jira-1.svg" 
                alt="Jira" 
                style={{ width: 16, height: 16 }} 
              />
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '6%',
      fixed: 'right',
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item 
                key="view" 
                icon={<InfoCircleOutlined />}
                onClick={() => {
                  setSelectedIssue(record);
                  setDetailModalVisible(true);
                }}
              >
                View Details
              </Menu.Item>
              {record.status === 'open' && (
                <>
                  <Menu.Item 
                    key="resolve" 
                    icon={<CheckCircleOutlined />}
                    onClick={() => {
                      setSelectedIssue(record);
                      setResolveModalVisible(true);
                    }}
                  >
                    Resolve
                  </Menu.Item>
                  <Menu.Item 
                    key="wontfix" 
                    icon={<CloseCircleOutlined />}
                    onClick={() => handleStatusUpdate(record._id, 'wont-fix')}
                  >
                    Won't Fix
                  </Menu.Item>
                </>
              )}
              {record.status !== 'open' && (
                <Menu.Item 
                  key="reopen" 
                  icon={<ExclamationCircleOutlined />}
                  onClick={() => handleStatusUpdate(record._id, 'open')}
                >
                  Reopen
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item 
                key="delete" 
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDeleteIssue(record._id)}
              >
                Delete Issue
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Issues"
              value={stats.total}
              prefix={<BugOutlined />}
              valueStyle={{ color: theme.primary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Open Issues"
              value={stats.openIssues}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Resolved"
              value={stats.byStatus?.resolved || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Critical Priority"
              value={stats.byPriority?.critical || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="1">
            <Space size="middle" wrap>
              {/* <Search
                placeholder="Search issues..."
                allowClear
                style={{ width: 250 }}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              /> */}
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 120 }}
                value={filters.status || undefined}
                onChange={(value) => setFilters({ ...filters, status: value })}
              >
                <Option value="open">Open</Option>
                <Option value="resolved">Resolved</Option>
                <Option value="wont-fix">Won't Fix</Option>
              </Select>
              <Select
                placeholder="Priority"
                allowClear
                style={{ width: 120 }}
                value={filters.priority || undefined}
                onChange={(value) => setFilters({ ...filters, priority: value })}
              >
                <Option value="critical">Critical</Option>
                <Option value="high">High</Option>
                <Option value="medium">Medium</Option>
                <Option value="low">Low</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => {
                fetchIssues();
                fetchStats();
              }}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Issues Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={issues}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} issues`,
            onChange: (page, pageSize) => setFilters({ ...filters, page, limit: pageSize })
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Card>

      {/* Issue Detail Modal */}
      <Modal
        title={
          <Space>
            {selectedIssue && getIssueTypeIcon(selectedIssue.issue.type)}
            <span>Issue Details</span>
          </Space>
        }
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          selectedIssue?.status === 'open' && (
            <Button 
              key="resolve" 
              type="primary" 
              onClick={() => {
                setDetailModalVisible(false);
                setResolveModalVisible(true);
              }}
            >
              Resolve Issue
            </Button>
          )
        ]}
      >
        {selectedIssue && (
          <div>
            {/* Issue Header */}
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>{selectedIssue.issue.title}</Title>
              <Space size="middle">
                <Tag color={getPriorityColor(selectedIssue.issue.priority)}>
                  {selectedIssue.issue.priority.toUpperCase()} PRIORITY
                </Tag>
                <Badge
                  status={
                    selectedIssue.status === 'open' ? 'processing' :
                    selectedIssue.status === 'resolved' ? 'success' : 'default'
                  }
                  text={selectedIssue.status.toUpperCase()}
                />
                <Text type="secondary">
                  <CalendarOutlined /> {moment(selectedIssue.createdAt).format('MMMM D, YYYY h:mm A')}
                </Text>
              </Space>
            </div>

            <Tabs defaultActiveKey="details">
              <Tabs.TabPane tab="Details" key="details">
                <Divider orientation="left">Issue Information</Divider>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text type="secondary">Type:</Text>
                    <br />
                    <Text strong>{formatIssueType(selectedIssue.issue.type)}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">API:</Text>
                    <br />
                    <Text strong>
                      {selectedIssue.apiDetails.group} / {selectedIssue.apiDetails.name}
                    </Text>
                  </Col>
                  <Col span={24}>
                    <Text type="secondary">Description:</Text>
                    <br />
                    <Paragraph>{selectedIssue.issue.description}</Paragraph>
                  </Col>
                  {selectedIssue.issue.expectedBehavior && (
                    <Col span={24}>
                      <Text type="secondary">Expected Behavior:</Text>
                      <br />
                      <Paragraph>{selectedIssue.issue.expectedBehavior}</Paragraph>
                    </Col>
                  )}
                  {selectedIssue.issue.actualBehavior && (
                    <Col span={24}>
                      <Text type="secondary">Actual Behavior:</Text>
                      <br />
                      <Paragraph>{selectedIssue.issue.actualBehavior}</Paragraph>
                    </Col>
                  )}
                </Row>

                <Divider orientation="left">Reporter</Divider>
                <Space>
                  <Avatar icon={<UserOutlined />} />
                  <div>
                    <Text strong>{selectedIssue.reporter.name}</Text>
                    <br />
                    <Text type="secondary">{selectedIssue.reporter.email}</Text>
                  </div>
                </Space>

                {selectedIssue.resolution && (
                  <>
                    <Divider orientation="left">Resolution</Divider>
                    <Alert
                      message="Issue Resolved"
                      description={
                        <div>
                          <Text>Resolved by {selectedIssue.resolution.resolvedBy.name}</Text>
                          <br />
                          <Text type="secondary">
                            {moment(selectedIssue.resolution.resolvedAt).format('MMMM D, YYYY h:mm A')}
                          </Text>
                          {selectedIssue.resolution.resolutionNotes && (
                            <>
                              <br />
                              <br />
                              <Text strong>Resolution Notes:</Text>
                              <br />
                              <Paragraph>{selectedIssue.resolution.resolutionNotes}</Paragraph>
                            </>
                          )}
                        </div>
                      }
                      type="success"
                      showIcon
                    />
                  </>
                )}
              </Tabs.TabPane>

              <Tabs.TabPane tab="Request/Response" key="data">
                <Divider orientation="left">Request Details</Divider>
                <div style={{ marginBottom: 16 }}>
                  <Tag color="blue">{selectedIssue.capturedData.request.method}</Tag>
                  <Text code>{selectedIssue.capturedData.request.url}</Text>
                </div>
                
                {selectedIssue.capturedData.request.headers && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Headers:</Text>
                    <pre style={{ 
                      background: theme.codeBg, 
                      padding: 12, 
                      borderRadius: 4,
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(selectedIssue.capturedData.request.headers, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedIssue.capturedData.request.body && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Request Body:</Text>
                    <pre style={{ 
                      background: theme.codeBg, 
                      padding: 12, 
                      borderRadius: 4,
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(selectedIssue.capturedData.request.body, null, 2)}
                    </pre>
                  </div>
                )}

                <Divider orientation="left">Response Details</Divider>
                <div style={{ marginBottom: 16 }}>
                  <Tag color={
                    selectedIssue.capturedData.response.status < 300 ? 'green' :
                    selectedIssue.capturedData.response.status < 400 ? 'orange' : 'red'
                  }>
                    {selectedIssue.capturedData.response.status}
                  </Tag>
                  <Text type="secondary">
                    Response time: {selectedIssue.capturedData.response.responseTime}ms
                  </Text>
                </div>

                <div>
                  <Text strong>Response Body:</Text>
                  <pre style={{ 
                    background: theme.codeBg, 
                    padding: 12, 
                    borderRadius: 4,
                    overflow: 'auto',
                    maxHeight: 400
                  }}>
                    {JSON.stringify(selectedIssue.capturedData.response.body, null, 2)}
                  </pre>
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab="Integrations" key="integrations">
                {selectedIssue.integrations.slack.sentToSlack ? (
                  <Alert
                    message="Sent to Slack"
                    description={
                      <div>
                        <Text>Channel: #{selectedIssue.integrations.slack.channelName}</Text>
                        <br />
                        {selectedIssue.integrations.slack.messageUrl && (
                          <a 
                            href={selectedIssue.integrations.slack.messageUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            View in Slack
                          </a>
                        )}
                      </div>
                    }
                    type="info"
                    showIcon
                    icon={<SlackOutlined />}
                    style={{ marginBottom: 16 }}
                  />
                ) : (
                  <Alert
                    message="Not sent to Slack"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {selectedIssue.integrations.jira.sentToJira ? (
                  <Alert
                    message="Created in Jira"
                    description={
                      <div>
                        <Text>Issue Key: {selectedIssue.integrations.jira.issueKey}</Text>
                        <br />
                        <Text>Project: {selectedIssue.integrations.jira.projectKey}</Text>
                        <br />
                        {selectedIssue.integrations.jira.issueUrl && (
                          <a 
                            href={selectedIssue.integrations.jira.issueUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            View in Jira
                          </a>
                        )}
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                ) : (
                  <Alert
                    message="Not created in Jira"
                    type="warning"
                    showIcon
                  />
                )}
              </Tabs.TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* Resolve Modal */}
      <Modal
        title="Resolve Issue"
        visible={resolveModalVisible}
        onCancel={() => {
          setResolveModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleResolve}
        >
          <Form.Item
            name="resolutionNotes"
            label="Resolution Notes"
            rules={[{ required: true, message: 'Please provide resolution details' }]}
          >
            <AntTextArea
              rows={4}
              placeholder="Describe how the issue was resolved..."
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Resolve Issue
              </Button>
              <Button onClick={() => {
                setResolveModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default IssuesSection;